import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

interface Category {
  name: string;
  url: string;
}

const categories: Category[] = [
  { name: 'Shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html' },
  { name: 'Hoodies/Sweaters', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html' },
  { name: 'T-Shirts', url: 'https://kaspreadsheet.com/search-913/name/Shoes.html' },
  { name: 'Jackets', url: 'https://kaspreadsheet.com/search-914/name/Shoes.html' },
  { name: 'Pants/Shorts', url: 'https://kaspreadsheet.com/search-915/name/Shoes.html' },
  { name: 'Headwear', url: 'https://kaspreadsheet.com/search-916/name/Shoes.html' },
  { name: 'Sets', url: 'https://kaspreadsheet.com/search-917/name/Shoes.html' },
  { name: 'Underwear/Underpants', url: 'https://kaspreadsheet.com/search-918/name/Shoes.html' },
  { name: 'Jersey', url: 'https://kaspreadsheet.com/search-919/name/Shoes.html' },
  { name: 'Accessories', url: 'https://kaspreadsheet.com/search-920/name/Shoes.html' },
  { name: 'Bags', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html' },
  { name: 'Belts', url: 'https://kaspreadsheet.com/search-922/name/Shoes.html' },
  { name: 'Perfumes', url: 'https://kaspreadsheet.com/search-923/name/Shoes.html' },
  { name: 'Other Stuff', url: 'https://kaspreadsheet.com/search-924/name/Shoes.html' },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);
  const toggleBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
      if (
        mobileRef.current &&
        !mobileRef.current.contains(e.target as Node) &&
        toggleBtnRef.current &&
        !toggleBtnRef.current.contains(e.target as Node)
      ) {
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const linkClass = (isScrolled: boolean) =>
    `px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
      isScrolled
        ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
        : 'text-background-200/90 hover:text-background-50 hover:bg-white/10'
    }`;

  const logoAccentClass = scrolled ? 'text-primary-500' : 'text-primary-300';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="flex items-center justify-between px-4 md:px-10 h-16 max-w-[1440px] mx-auto">
        <div className="flex items-center gap-4 md:gap-8">
          <Link
            to="/"
            className={`font-heading text-xl md:text-2xl font-semibold tracking-wide whitespace-nowrap cursor-pointer transition-colors duration-200 ${
              scrolled ? 'text-foreground-900' : 'text-background-50'
            }`}
          >
            itaobuy<span className={logoAccentClass}>.</span>skin
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link to="/" className={linkClass(scrolled)}>Home</Link>
            <Link to="/shopping-guide" className={linkClass(scrolled)}>Shopping Guide</Link>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div ref={dropdownRef} className="relative hidden md:block">
            <button
              onClick={toggleDropdown}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-colors duration-200 ${
                scrolled
                  ? 'bg-accent-100 text-foreground-800 hover:bg-accent-200'
                  : 'bg-white/15 text-background-50 hover:bg-white/25'
              }`}
            >
              <span>All Categories</span>
              <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                <i className="ri-arrow-down-s-line text-sm"></i>
              </span>
            </button>

            {isOpen && (
              <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 rounded-lg border border-background-200/60 py-2 max-h-80 overflow-y-auto z-50">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setIsOpen(false)}
                    className="block px-5 py-2.5 text-sm text-foreground-700 hover:bg-accent-50 hover:text-foreground-900 transition-colors duration-150 whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className={`hidden md:flex items-center justify-center w-9 h-9 rounded-full cursor-pointer transition-colors duration-200 ${
              scrolled
                ? 'bg-foreground-900 text-background-50 hover:bg-foreground-700'
                : 'bg-white/15 text-background-50 hover:bg-white/25'
            }`}
            aria-label="Go to main website"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-external-link-line text-sm"></i>
            </span>
          </a>

          <button
            ref={toggleBtnRef}
            onClick={() => setMobileOpen((prev) => !prev)}
            className={`md:hidden flex items-center justify-center w-9 h-9 rounded-full cursor-pointer transition-colors duration-200 ${
              scrolled
                ? 'text-foreground-800 hover:bg-background-100'
                : 'text-background-50 hover:bg-white/10'
            }`}
            aria-label="Toggle menu"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-lg`}></i>
            </span>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div ref={mobileRef} className="md:hidden bg-background-50 border-t border-background-200/60">
          <div className="px-4 py-4 space-y-0.5 max-h-[70vh] overflow-y-auto">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-800 text-sm font-medium hover:bg-accent-50 transition-colors"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-home-4-line"></i>
              </span>
              Home
            </Link>
            <Link
              to="/shopping-guide"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-800 text-sm font-medium hover:bg-accent-50 transition-colors"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-book-open-line"></i>
              </span>
              Shopping Guide
            </Link>
            <hr className="my-2 border-background-200/60" />
            <p className="px-4 py-2 text-xs text-foreground-400 font-semibold uppercase tracking-widest">Categories</p>
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-700 text-sm hover:bg-accent-50 hover:text-foreground-900 transition-colors"
              >
                <span className="w-5 h-5 flex items-center justify-center text-accent-500">
                  <i className="ri-arrow-right-up-line text-xs"></i>
                </span>
                {cat.name}
              </a>
            ))}
            <hr className="my-2 border-background-200/60" />
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-colors"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-shopping-bag-3-line"></i>
              </span>
              Go to Kaspreadsheet &rarr;
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}

export { categories };